import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { sendNotification } from "../../features/notificationSlice/notificationSlice";
import { showModal } from "../../features/statusModal/statusModal";
import Button from "../button/Button";
import NotificationIcon from "../icons/NotificationIcon";
import TextArea from "../inputs/textArea/TextArea";
import TextInput from "../inputs/textInput/TextInput";
import Loader from "../loader/Loader";
import Portal from "../portal/Portal";
import TextWrapper from "../textWrapper/TextWrapper";
import styles from "./sentNotification.module.css";
import SelectInput from "../inputs/selectInput/SelectInput";
import { fetchPaginatedTournament } from "../../features/tournaments/tournamentsSlice";

const DEFAULT_BEHAVIOR_OPTIONS = [
  { value: "general", label: "عامة" },
  { value: "scroll_to_tournament", label: "الانتقال الى البطولة" },
  { value: "open_link", label: "فتح الرابط" },
  { value: "room_information", label: "ارسال معلومات الروم" },
];

const ALL_TARGET_BEHAVIOR_OPTIONS = DEFAULT_BEHAVIOR_OPTIONS.filter(
  (option) => option.value !== "room_information",
);

const TABLE_TARGET_BEHAVIOR_OPTIONS = [{ value: "general", label: "عامة" }];

const BEHAVIOR_OPTIONS_BY_TARGET_TYPE = {
  all: ALL_TARGET_BEHAVIOR_OPTIONS,
  table: TABLE_TARGET_BEHAVIOR_OPTIONS,
};

const notificationBehaviorMap = {
  general: {
    getExtraData: () => ({}),
  },
  scroll_to_tournament: {
    getExtraData: ({ selectedTournament, contextTournamentId }) => ({
      kind: "scroll_to_tournament",
      tournamentId: selectedTournament || contextTournamentId || null,
    }),
  },
  open_link: {
    getExtraData: ({ toOpenLink }) => ({
      kind: "open_link",
      link: toOpenLink,
    }),
  },
  room_information: {
    getExtraData: ({ gameRoomInfo, contextTournamentId, contextTournamentName }) => {
      const normalizedTournamentName = String(
        contextTournamentName || "",
      ).trim();
      const roomInfo = {
        ...(gameRoomInfo || {}),
      };

      if (normalizedTournamentName && !roomInfo.tournamentName) {
        roomInfo.tournamentName = normalizedTournamentName;
      }

      return {
        kind: "room_information",
        roomInfo,
        ...(contextTournamentId ? { tournamentId: contextTournamentId } : {}),
        ...(normalizedTournamentName
          ? { tournamentName: normalizedTournamentName }
          : {}),
      };
    },
  },
};

const SendNotification = ({
  targetType,
  type,
  targetId,
  contextTournamentId = "",
  contextTournamentName = "",
  iconOnly = false,
  tooltip = "ارسال اشعار",
  className = "",
  iconClassName = "",
}) => {
  const [title, setTitle] = useState("");
  const [enTitle, setEnTitle] = useState("");

  const [body, setBody] = useState("");
  const [enBody, setEnBody] = useState("");

  const [behaviorType, setBehaviorType] = useState("general");

  const [selectedTournament, setSelectedTournament] = useState("");
  const [toOpenLink, setToOpenLink] = useState("");
  const [gameRoomInfo, setGameRoomInfo] = useState({
    roomId: "",
    roomPass: "",
    roomName: "",
  });

  const [showNotification, setShowNotification] = useState(false);

  const dispatch = useDispatch();

  const { sendStatus } = useSelector((state) => state.notification);
  const { list: tournaments, fetchStatus } = useSelector(
    (state) => state.tournaments,
  );
  const behaviorOptions =
    BEHAVIOR_OPTIONS_BY_TARGET_TYPE[targetType] || DEFAULT_BEHAVIOR_OPTIONS;

  const clearAllFieldsAndCloseModal = () => {
    setTitle("");
    setEnTitle("");
    setBody("");
    setEnBody("");
    setBehaviorType("general");
    setToOpenLink("");
    setSelectedTournament("");
    setGameRoomInfo({ roomId: "", roomPass: "", roomName: "" });
    setShowNotification(false);
  };

  const handleSend = () => {
    const behavior =
      notificationBehaviorMap[behaviorType] || notificationBehaviorMap.general;

    if (
      behaviorType === "scroll_to_tournament" &&
      !selectedTournament &&
      !contextTournamentId
    ) {
      dispatch(
        showModal({
          message: "يرجى اختيار بطولة عند استخدام الانتقال الى البطولة",
          status: "error",
        }),
      );
      return;
    }

    if (behaviorType === "open_link" && toOpenLink.trim() === "") {
      dispatch(
        showModal({
          message: "يرجى إدخال رابط صالح",
          status: "error",
        }),
      );
      return;
    }

    if (
      sendStatus !== "loading" &&
      title.trim() !== "" &&
      body.trim() !== "" &&
      enTitle.trim() !== "" &&
      enBody.trim() !== ""
    ) {
      const behaviorContext = {
        selectedTournament,
        toOpenLink,
        gameRoomInfo,
        contextTournamentId,
        contextTournamentName,
      };

      dispatch(
        sendNotification({
          title,
          enTitle,
          body,
          enBody,
          type,
          targetType,
          targetId,
          extraData: behavior.getExtraData(behaviorContext),
        }),
      );
      clearAllFieldsAndCloseModal();
    }
  };

  useEffect(() => {
    const isCurrentBehaviorAllowed = behaviorOptions.some(
      (option) => option.value === behaviorType,
    );
    if (!isCurrentBehaviorAllowed) {
      setBehaviorType("general");
    }
  }, [behaviorOptions, behaviorType]);

  useEffect(() => {
    if (
      fetchStatus === "idle" &&
      tournaments?.length === 0 &&
      behaviorType === "scroll_to_tournament"
    ) {
      dispatch(fetchPaginatedTournament({ page: 1 }));
    }
  }, [fetchStatus, tournaments, behaviorType, dispatch]);

  useEffect(() => {
    if (!showNotification) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [showNotification]);

  return (
    <div className={styles.outerContainer}>
      <div
        className={`${styles.container} ${iconOnly ? styles.iconOnly : ""} ${className}`}
        onClick={() => setShowNotification(true)}
        title={tooltip}
      >
        <NotificationIcon
          className={`${styles.notificationIcon} ${iconClassName}`}
        />
        {!iconOnly && <TextWrapper textStyle="textXSmall">ارسال اشعار</TextWrapper>}
      </div>
      {showNotification && (
        <Portal>
          <div className={styles.notificationInfoWrapper}>
            <div
              className={styles.blurBackGround}
              onClick={clearAllFieldsAndCloseModal}
            ></div>
            <div className={styles.notificationModalWrapper}>
              <div className={styles.modalHeader}>
                <button
                  type="button"
                  className={styles.closeButton}
                  onClick={clearAllFieldsAndCloseModal}
                  aria-label="اغلاق نافذة ارسال الاشعار"
                >
                  <span aria-hidden="true" className={styles.closeGlyph}>
                    x
                  </span>
                </button>
                <TextWrapper textStyle="textMid">ارسال اشعار</TextWrapper>
              </div>
              <div className={styles.formWrapper}>
                <SelectInput
                  placeHolder={"نوع الاشعار"}
                  value={behaviorType}
                  setValue={setBehaviorType}
                  noPlaceHolder={true}
                >
                  {behaviorOptions.map((option) => (
                    <option value={option.value} key={option.value}>
                      {option.label}
                    </option>
                  ))}
                </SelectInput>

                {behaviorType === "scroll_to_tournament" && (
                  <SelectInput
                    placeHolder={"اختيار البطولة"}
                    value={selectedTournament}
                    setValue={setSelectedTournament}
                  >
                    {tournaments?.map((tournament) => {
                      return (
                        <option value={tournament?._id} key={tournament?._id}>
                          {tournament?.name}
                        </option>
                      );
                    })}
                  </SelectInput>
                )}

                {behaviorType === "open_link" && (
                  <TextInput
                    height="50px"
                    width="100%"
                    borderRadius="10px"
                    bordered
                    placeHolder="الرابط"
                    bgColor={"grey"}
                    value={toOpenLink}
                    onChange={(e) => setToOpenLink(e.target.value)}
                  />
                )}

                {behaviorType === "room_information" && (
                  <>
                    <TextInput
                      height="50px"
                      width="100%"
                      borderRadius="10px"
                      bordered
                      placeHolder="اسم الروم"
                      bgColor={"grey"}
                      value={gameRoomInfo?.roomName}
                      onChange={(e) =>
                        setGameRoomInfo((prev) => {
                          return { ...prev, roomName: e.target.value };
                        })
                      }
                    />
                    <TextInput
                      height="50px"
                      width="100%"
                      borderRadius="10px"
                      bordered
                      placeHolder="ايدي الروم"
                      bgColor={"grey"}
                      value={gameRoomInfo?.roomId}
                      onChange={(e) =>
                        setGameRoomInfo((prev) => {
                          return { ...prev, roomId: e.target.value };
                        })
                      }
                    />
                    <TextInput
                      height="50px"
                      width="100%"
                      borderRadius="10px"
                      bordered
                      placeHolder="باسوورد الروم"
                      bgColor={"grey"}
                      value={gameRoomInfo?.roomPass}
                      onChange={(e) =>
                        setGameRoomInfo((prev) => {
                          return { ...prev, roomPass: e.target.value };
                        })
                      }
                    />
                  </>
                )}

                <div className={styles.horizontalLine}></div>

                <TextInput
                  height="50px"
                  width="100%"
                  borderRadius="10px"
                  bordered
                  placeHolder="عنوان الاشعار"
                  bgColor={"grey"}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />

                <TextArea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeHolder="محتوى الاشعار"
                />

                <div className={styles.horizontalLine}></div>

                <TextInput
                  height="50px"
                  width="100%"
                  borderRadius="10px"
                  bordered
                  placeHolder="عنوان الاشعار بالانجليزية"
                  bgColor={"grey"}
                  value={enTitle}
                  onChange={(e) => setEnTitle(e.target.value)}
                />

                <TextArea
                  value={enBody}
                  onChange={(e) => setEnBody(e.target.value)}
                  placeHolder="محتوى الاشعار بالانجليزية"
                />
              </div>
              <div className={styles.btnsWrapper}>
                {sendStatus === "loading" ? (
                  <Loader />
                ) : (
                  <Button
                    variant="greenVariant"
                    size="midSize"
                    functionallity={handleSend}
                  >
                    <TextWrapper textStyle="textMid">تأكيد الأمر</TextWrapper>
                  </Button>
                )}
                <Button
                  variant="redVariant"
                  size="midSize"
                  functionallity={clearAllFieldsAndCloseModal}
                >
                  <TextWrapper textStyle="textMid">الغاء الامر</TextWrapper>
                </Button>
              </div>
            </div>
          </div>
        </Portal>
      )}
    </div>
  );
};

export default SendNotification;
